// ============================================================
//  src/pages/billing/BillingPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBillingOverview, getBillingHistory, upgradePlan } from '../../api/client';

const PAGE_SIZE = 10;

function fmtNaira(amount) {
  const n = Number(amount) || 0;
  return '₦' + n.toLocaleString('en-NG', { maximumFractionDigits: 0 });
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getCountdown(expiresAt) {
  if (!expiresAt) return null;
  const now  = Date.now();
  const end  = new Date(expiresAt.replace(' ', 'T')).getTime();
  const diff = end - now;
  if (diff <= 0) return { expired: true, label: 'Expired', color: 'var(--danger)' };
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  if (days > 30)  return { expired: false, label: `${days} days remaining`, color: 'var(--success)' };
  if (days >= 7)  return { expired: false, label: `${days} days remaining`, color: 'var(--warning)' };
  if (days >= 1)  return { expired: false, label: `${days}d ${hours}h remaining`, color: 'var(--danger)' };
  return { expired: false, label: `${hours}h ${mins}m remaining`, color: 'var(--danger)' };
}

const STATUS_META = {
  success:   { bg: 'rgba(0,196,140,0.10)', color: '#00A374',       label: 'Successful', dot: '#00C88C' },
  pending:   { bg: 'rgba(255,149,0,0.10)', color: 'var(--warning)', label: 'Pending',    dot: '#FF9500' },
  failed:    { bg: 'rgba(255,59,48,0.10)', color: 'var(--danger)',  label: 'Failed',     dot: '#FF3B30' },
  cancelled: { bg: 'var(--surface)',       color: 'var(--text-muted)', label: 'Cancelled', dot: '#9AA0C0' },
};

const STORAGE_KEY = 'pocketfi_pending_payment_id';

// ── Pagination controls ───────────────────────────────────────
function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) pages.push(i);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginTop: 16, flexWrap: 'wrap', gap: 10,
    }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total} transactions
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          style={btnStyle(false, page === 1)}
        >← Prev</button>

        {pages.map(p => (
          <button
            key={p}
            onClick={() => onChange(p)}
            style={btnStyle(p === page, false)}
          >{p}</button>
        ))}

        <button
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          style={btnStyle(false, page === totalPages)}
        >Next →</button>
      </div>
    </div>
  );
}

function btnStyle(active, disabled) {
  return {
    minWidth: 36, height: 36, padding: '0 10px',
    borderRadius: 8, border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    background: active ? 'var(--primary)' : 'var(--bg-card)',
    color: active ? '#fff' : disabled ? 'var(--text-muted)' : 'var(--text-dark)',
    fontSize: 13, fontWeight: active ? 800 : 600, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1, fontFamily: 'inherit', transition: 'all 0.15s',
  };
}

// ── Main ─────────────────────────────────────────────────────
export default function BillingPage() {
  const [overview,  setOverview]  = useState(null);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [page,      setPage]      = useState(1);

  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [ov, hist] = await Promise.all([getBillingOverview(), getBillingHistory()]);
      setOverview(ov.data);
      setHistory(hist.data.payments || []);
    } catch {
      toast.error('Could not load billing details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleUpgrade(plan) {
    if (upgrading) return;
    setUpgrading(plan.plSlug);
    try {
      const res = await upgradePlan(plan.plSlug);
      if (res.data.requires_payment) {
        localStorage.setItem(STORAGE_KEY, res.data.payment_id);
        window.location.href = res.data.payment_link;
      } else {
        toast.success(res.data.message || 'Plan updated');
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not start upgrade');
    } finally {
      setUpgrading(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        <span className="spinner spinner-dark" /> Loading billing details…
      </div>
    );
  }

  const current       = overview?.current_plan;
  const plans         = overview?.plans || [];
  const pocketfiReady = overview?.pocketfi_ready;

  // Pagination slice
  const totalTx    = history.length;
  const paginated  = history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Billing & Plans</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13.5, marginBottom: 24 }}>
        Manage your subscription and view available plans.
      </p>

      {!pocketfiReady && (
        <div className="page-card" style={{ borderColor: 'var(--warning)' }}>
          <div className="page-card-body" style={{ fontSize: 13, color: 'var(--warning)' }}>
            ⚠️ Online payments are not yet configured. Plan upgrades requiring payment are temporarily unavailable.
          </div>
        </div>
      )}

      {/* ── Current Plan ── */}
      <div className="page-card">
        <div className="page-card-hdr">Current Plan</div>
        <div className="page-card-body">
          {current ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{current.plName}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                  {current.plPrice > 0
                    ? `${fmtNaira(current.plPrice)} / ${current.plInterval ? `${current.plInterval} days` : 'one-time'}`
                    : current.plInterval ? `Free · ${current.plInterval}-day access` : 'Free forever'}
                </div>
                {current.subExpiresAt && (() => {
                  const cd = getCountdown(current.subExpiresAt);
                  return (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        Expires on {fmtDate(current.subExpiresAt)}
                      </div>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 12, fontWeight: 700, color: cd.color,
                        background: `${cd.color}18`, padding: '3px 10px',
                        borderRadius: 20, width: 'fit-content',
                      }}>
                        <span style={{ fontSize: 10 }}>⏱</span> {cd.label}
                      </div>
                    </div>
                  );
                })()}
              </div>
              {current.plFeatures?.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)' }}>
                  {current.plFeatures.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 13.5 }}>
              You don't have an active plan yet.
            </div>
          )}
        </div>
      </div>

      {/* ── Available Plans ── */}
      <div className="page-card">
        <div className="page-card-hdr">Available Plans</div>
        <div className="page-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            {plans.map(plan => {
              const isCurrent = current && current.plSlug === plan.plSlug;
              const isFree    = Number(plan.plPrice) <= 0;
              return (
                <div key={plan.plSlug} style={{
                  border: `1.5px solid ${isCurrent ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-lg)', padding: 16,
                  display: 'flex', flexDirection: 'column', gap: 10,
                  background: isCurrent ? 'var(--primary-light)' : 'transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{plan.plName}</span>
                    {isCurrent && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: 'var(--primary)',
                        background: 'rgba(0,44,237,0.1)', padding: '2px 8px', borderRadius: 20,
                      }}>Current</span>
                    )}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>
                    {isFree ? 'Free' : fmtNaira(plan.plPrice)}
                    {!isFree && plan.plInterval && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                        {' '}/ {plan.plInterval} days
                      </span>
                    )}
                  </div>
                  {plan.plDescription && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{plan.plDescription}</div>
                  )}
                  {plan.plFeatures?.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>
                      {plan.plFeatures.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}
                  <button
                    className={isCurrent ? 'btn btn-outline' : 'btn btn-primary'}
                    disabled={isCurrent || upgrading === plan.plSlug || (!isFree && !pocketfiReady)}
                    onClick={() => handleUpgrade(plan)}
                    style={{ marginTop: 'auto' }}
                    title={!isFree && !pocketfiReady ? 'Online payments not configured yet' : ''}
                  >
                    {upgrading === plan.plSlug
                      ? <><span className="spinner" /> Redirecting…</>
                      : isCurrent ? 'Current Plan' : isFree ? 'Switch to Free' : 'Upgrade'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Payment History ── */}
      <div className="page-card">
        <div className="page-card-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Payment History</span>
          {totalTx > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              padding: '2px 10px', borderRadius: 20,
            }}>
              {totalTx} total
            </span>
          )}
        </div>
        <div className="page-card-body">
          {history.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13.5, textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🧾</div>
              No payments yet.
            </div>
          ) : (
            <>
              {/* Table header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto',
                padding: '6px 14px', marginBottom: 6,
                fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                <span>Plan</span>
                <span style={{ textAlign: 'right', marginRight: 16 }}>Amount</span>
                <span style={{ textAlign: 'right' }}>Status</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {paginated.map((p, idx) => {
                  const s = STATUS_META[p.payStatus] || STATUS_META.pending;
                  const isSuccess = p.payStatus === 'success';
                  return (
                    <div key={p.payId} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto',
                      alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      border: `1.5px solid ${isSuccess ? 'rgba(0,200,140,0.15)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-sm)',
                      background: isSuccess ? 'rgba(0,200,140,0.02)' : 'var(--bg-card)',
                      animation: `fadeIn 0.15s ease ${idx * 0.03}s both`,
                    }}>
                      {/* Plan info */}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-dark)' }}>
                          {p.plName}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          {fmtDate(p.payCreatedAt)}
                          {p.payPocketfiId && (
                            <span style={{ marginLeft: 8, fontFamily: 'monospace', opacity: 0.6 }}>
                              · {p.payPocketfiId.slice(-8)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Amount */}
                      <span style={{
                        fontWeight: 800, fontSize: 14, color: 'var(--text-dark)',
                        marginRight: 8, whiteSpace: 'nowrap',
                      }}>
                        {fmtNaira(p.payAmount)}
                      </span>

                      {/* Status badge */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 11, fontWeight: 700, padding: '4px 10px',
                        borderRadius: 20, background: s.bg, color: s.color,
                        whiteSpace: 'nowrap',
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: s.dot, flexShrink: 0,
                          boxShadow: isSuccess ? `0 0 0 2px ${s.dot}30` : 'none',
                        }} />
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              <Pagination
                page={page}
                total={totalTx}
                pageSize={PAGE_SIZE}
                onChange={p => { setPage(p); }}
              />
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }`}</style>
    </div>
  );
}